import React from 'react';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import useUI from 'hooks/use-ui';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { BrandCard } from 'components/brand-card';
import { SearchBarFilter } from 'components/core';
import { useBrandsFilter } from 'src/components/menu-sidebar/brands-filter/use-brands-filter';
import { PRODUCT_GROUP_SIZES } from 'src/components/product-list/product-list.constants';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

type Brand = {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
};

type BrandsGridProps = {
  brands: Brand[];
  className?: string;
};

export const BrandsGrid = ({ brands, className }: BrandsGridProps): JSX.Element => {
  const { setSearch, search, displaySearchBar, convertedBrands, handleBrandClickEvent } = useBrandsFilter(brands);
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const useKioskProStyles = isKiosk && isKioskProEnabled;

  return (
    <Container className={className} $useKioskProStyles={useKioskProStyles}>
      <Header>
        <Title $useKioskProStyles={useKioskProStyles}>Brands</Title>
        {displaySearchBar && (
          <SearchContainer>
            <StyledSearchBarFilter
              placeholder={`Search through ${brands.length} brands`}
              value={search}
              onChange={(e) => setSearch(_.toLower(e.target.value))}
              $useKioskProStyles={useKioskProStyles}
            />
          </SearchContainer>
        )}
      </Header>

      {convertedBrands.length === 0 && search ? (
        <NoResults>
          <NoResultsTitle>No brands found matching "{search}"</NoResultsTitle>
        </NoResults>
      ) : (
        <Grid breakpoints={PRODUCT_GROUP_SIZES.grid}>
          {convertedBrands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              href={brand.href}
              onClick={() => handleBrandClickEvent(brand.name)}
            />
          ))}
        </Grid>
      )}
    </Container>
  );
};

const Container = styled.div<{ $useKioskProStyles: boolean }>`
  padding: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '24px' : '46px 24px 24px 24px')};
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
`;

const StyledSearchBarFilter = styled(SearchBarFilter)<{ $useKioskProStyles: boolean }>`
  && {
    margin: 0;
    border-radius: 40px;
    width: 100%;
  }

  label {
    padding: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '12px' : '6px')};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  input {
    font-size: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '24px' : '13px')};
    font-weight: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '600' : '400')};
    padding: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '24px 24px 24px 0' : '12px 12px 12px 0')};
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    input {
      font-size: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '20px' : '13px')};
    }
  }
`;

type Breakpoint = {
  breakpoint: number;
  rowSize: number;
  adPosition: number;
};

type GridProps = {
  breakpoints: Record<string, Breakpoint>;
};

const Grid = styled.div<GridProps>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(${({ breakpoints }) => breakpoints.sm.rowSize}, 1fr);
  margin: 0 0 20px;

  ${({ breakpoints }) =>
    Object.values(breakpoints).map(
      (breakpoint: Breakpoint) => css`
        @media (min-width: ${breakpoint.breakpoint}px) {
          grid-template-columns: repeat(${breakpoint.rowSize}, 1fr);
        }
      `
    )}
`;

const Title = styled.h1<{ $useKioskProStyles: boolean }>`
  font-size: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '36px' : '24px')};
  font-weight: bold;
  color: #242526;
  margin: 0 0 8px 0;
  text-align: center;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '28px' : '24px')};
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: ${({ $useKioskProStyles }) => ($useKioskProStyles ? '28px' : '24px')};
  }
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
`;

const NoResultsTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #999;
  margin: 0;
`;
